import { RadioGroup, RadioGroupItem } from "@/components/podkit/forms/RadioListField";
import { Text } from "@/components/podkit/typography/Text";
import { useAtomValue, useSetAtom, type PrimitiveAtom, type WritableAtom } from "jotai";
import type { DraftStep } from "@/routes/automations/automation-edit-page/types";
import {
    AutomationCommandStepEditor,
    AutomationPromptStepEditor,
} from "@/routes/automations/automation-edit-page/AutomationStepEditors";
import { AutomationPullRequestStepEditor } from "@/routes/automations/automation-edit-page/AutomationPullRequestStepEditor";
import type { WorkflowEditFormStepType } from "@/components/automations/automation-edit-form-data";
import type { FC } from "react";
import { Button } from "@/components/flexkit/Button";
import { IconConsoleSimple } from "central-icons/IconConsoleSimple";
import { IconBubbleText6 } from "central-icons/IconBubbleText6";
import { IconPullRequest } from "central-icons/IconPullRequest";

type AutomationEmptyStepCreatorProps = {
    draftStepAtom: PrimitiveAtom<DraftStep | null>;
    changeDraftStepTypeAtom: WritableAtom<null, [WorkflowEditFormStepType], void>;
    upsertDraftStepIntoStepsAtom: WritableAtom<null, [], boolean | undefined>;
};

/**
 * Displayed when automation details page is in edit mode and no steps are present.
 * Presents a UI for creating the initial step of the automation.
 */
export const AutomationEmptyStepCreator: FC<AutomationEmptyStepCreatorProps> = ({
    draftStepAtom,
    changeDraftStepTypeAtom,
    upsertDraftStepIntoStepsAtom,
}) => {
    const draftStep = useAtomValue(draftStepAtom);
    const upsertDraftStepIntoSteps = useSetAtom(upsertDraftStepIntoStepsAtom);

    if (!draftStep) {
        return null;
    }

    let contentEditor: React.ReactNode | null;
    switch (draftStep.type) {
        case "command":
            contentEditor = <AutomationCommandStepEditor contentAtom={draftStep.content} />;
            break;
        case "prompt":
            contentEditor = <AutomationPromptStepEditor contentAtom={draftStep.content} />;
            break;
        case "pr":
            contentEditor = <AutomationPullRequestStepEditor draft={draftStep} />;
            break;
    }

    const addStep = () => {
        upsertDraftStepIntoSteps();
    };

    return (
        <div className="mt-4 flex max-w-screen-lg flex-1 flex-col rounded-xl border border-border-base bg-surface-primary p-4 shadow-md">
            <Text className="text-md font-medium">No steps yet</Text>
            <Text className="text-content-strong">
                Define what Ona will do when this workflow runs. Add a command, prompt, or pull request action to get
                started.
            </Text>
            <StepTypeRadioGroup changeDraftStepTypeAtom={changeDraftStepTypeAtom} draft={draftStep} />
            {contentEditor}
            <div className="mt-3 flex justify-end gap-2">
                <Button variant="primary" size="sm" onClick={addStep} data-tracking-id="add">
                    Add
                </Button>
            </div>
        </div>
    );
};

type StepTypeRadioGroupProps = {
    changeDraftStepTypeAtom: WritableAtom<null, [WorkflowEditFormStepType], void>;
    draft: DraftStep;
};

const StepTypeRadioGroup: FC<StepTypeRadioGroupProps> = ({ changeDraftStepTypeAtom, draft }) => {
    const changeDraftStepType = useSetAtom(changeDraftStepTypeAtom);
    return (
        <RadioGroup
            value={draft.type}
            onValueChange={(value) => changeDraftStepType(value as WorkflowEditFormStepType)}
            className="my-3 flex flex-row gap-4 text-base"
        >
            <label className="flex flex-row items-center gap-2">
                <RadioGroupItem value="command" />
                <span>
                    <IconConsoleSimple size={20} />
                </span>
                <span className="translate-y-px">Command</span>
            </label>
            <label className="flex flex-row items-center gap-2">
                <RadioGroupItem value="prompt" />
                <span>
                    <IconBubbleText6 size={20} />
                </span>
                <span className="translate-y-[0.5px]">Prompt</span>
            </label>
            <label className="flex flex-row items-center gap-2">
                <RadioGroupItem value="pr" />
                <span>
                    <IconPullRequest size={20} />
                </span>
                <span className="translate-y-px">Create Pull Request</span>
            </label>
        </RadioGroup>
    );
};
